import { MotionImage, MotionText } from '@shared/atoms';
import React, { useCallback, useState } from 'react';

import chatIcon from '@assets/images/earlyWarning/chat-icon.svg';
import thoughtIcon from '@assets/images/icons/thought-icon.svg';

import { formatDateStringSplitDots } from '@utils/date.util';
import { InstantMessagingModal } from '../InstantMessagingModal/InstantMessagingModal';
import { SystemButton } from '../SystemButton/SystemButton';
import { ViewThoughtCommentModal } from '../ViewThoughtCommentModal/ViewThoughtCommentModal';
import styles from './index.module.scss';

interface ShareThoughtAndSpeakUpCardProps {
  id: number;
  mainTitle: string;
  commentDate: string;
  comment: string;
  responseDate: string | null;
  response: string;
  buttonText?: string;
  questionnaireName?: string;
  type?: 'thoughts' | 'speakUps';
  questionLabel?: string;
  score?: number;
  hideResponseSection?: boolean;
}

interface TruncatedTextProps {
  text: string;
  maxLength: number;
  className: string;
  isBlockquote: boolean;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  maxLength,
  className,
  isBlockquote = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text.length <= maxLength) {
    if (isBlockquote) {
      return <blockquote className={className}>&apos;{text}&apos;</blockquote>;
    }

    return <MotionText as="p" className={className} text={text} />;
  }

  const truncatedText = text.substring(0, maxLength);
  const remainingText = text.substring(maxLength);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  if (isBlockquote) {
    return (
      <blockquote className={className}>
        &apos;{truncatedText}
        {!isExpanded && '...'}
        {isExpanded && remainingText}&apos;
        <span
          style={{
            color: '#019df2',
            cursor: 'pointer',
            fontWeight: 600,
            marginLeft: '8px',
            textDecoration: 'none',
          }}
          onClick={toggleExpanded}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          aria-label={isExpanded ? 'Show less text' : 'Show more text'}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </span>
      </blockquote>
    );
  }

  return (
    <div className={className} style={{ marginBottom: '20px' }}>
      {truncatedText}
      {!isExpanded && '...'}
      {isExpanded && remainingText}
      <span
        style={{
          color: '#019df2',
          cursor: 'pointer',
          fontWeight: 600,
          marginLeft: '8px',
          textDecoration: 'none',
        }}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label={isExpanded ? 'Show less text' : 'Show more text'}
        onMouseEnter={(e) => {
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </span>
    </div>
  );
};

export const ShareThoughtAndSpeakUpCard: React.FC<ShareThoughtAndSpeakUpCardProps> = ({
  id,
  mainTitle,
  commentDate,
  comment,
  responseDate,
  response,
  buttonText = 'View & Message',
  questionnaireName,
  type = 'speakUps',
  questionLabel,
  score,
  hideResponseSection,
}) => {
  const [openViewCommentsModal, setOpenViewCommentsModal] = useState(false);

  const handleOpenViewCommentModal = useCallback(() => {
    setOpenViewCommentsModal(true);
  }, []);

  const handleCloseViewCommentModal = useCallback(() => {
    setOpenViewCommentsModal(false);
  }, []);

  return (
    <div className={styles.cardContainer}>
      <div className={`${styles.header}`}>
        <div
          className={
            type === 'thoughts' ? styles.thoughtIconContainer : styles.speakUpIconContainer
          }
        >
          <MotionImage
            src={type === 'speakUps' ? chatIcon : thoughtIcon}
            alt="Chat Icon"
            className={styles.chatIcon}
          />
          <MotionText as="h3" className={styles.title} text={mainTitle} />
        </div>

        {type === 'speakUps' && (
          <div className={styles.rightSide}>
            {questionnaireName && (
              <div className={styles.questionnaireNameTag}>
                <MotionText as="span" className={styles.span} text={questionnaireName} />
              </div>
            )}
          </div>
        )}

        {type === 'thoughts' && (
          <div className={styles.rightSide}>
            {questionnaireName && (
              <div className={styles.questionnaireNameTag}>
                <MotionText as="span" className={styles.span} text={questionnaireName} />
              </div>
            )}
            {questionLabel && score && (
              <div className={styles.cardQuestName}>
                <p>
                  {questionLabel} &nbsp; <span>{score}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <MotionText
        as="h4"
        className={
          type === 'thoughts'
            ? styles.commentDateThought
            : questionnaireName
            ? styles.commentDateSpeakUp
            : styles.commentDateSpeakUpNoQuestion
        }
        text={`Your comment on ${formatDateStringSplitDots(commentDate)}`}
      />
      {/* <blockquote className={styles.commentText}>‘{comment}’</blockquote> */}
      <TruncatedText text={comment} maxLength={300} className={styles.commentText} isBlockquote />

      {!hideResponseSection && (
        <div className={styles.responseContainer}>
          <MotionText
            as="h4"
            className={styles.responseDate}
            text={
              responseDate
                ? `Response on ${formatDateStringSplitDots(responseDate)}`
                : 'Awaiting Response'
            }
          />
          {/* <MotionText as="p" className={styles.responseText} text={` ${response}`} /> */}
          <TruncatedText
            text={response}
            maxLength={300}
            className={styles.responseText}
            isBlockquote={false}
          />
        </div>
      )}

      {!hideResponseSection && (
        <div className={styles.footer}>
          <SystemButton
            variant="primaryDarkSkyBlue"
            onClick={handleOpenViewCommentModal}
            disabled={type === 'thoughts' && !responseDate}
          >
            {buttonText}
          </SystemButton>
        </div>
      )}

      {type === 'speakUps' ? (
        <InstantMessagingModal
          title={questionnaireName}
          isOpen={openViewCommentsModal}
          onClose={handleCloseViewCommentModal}
          speakUpId={id}
          isUserInterface
        />
      ) : (
        <ViewThoughtCommentModal
          title={questionnaireName}
          isOpen={openViewCommentsModal}
          onClose={handleCloseViewCommentModal}
        >
          {response && response.split('\n').join('<br/>')}
        </ViewThoughtCommentModal>
      )}
    </div>
  );
};

ShareThoughtAndSpeakUpCard.defaultProps = {
  buttonText: 'View & Message',
  questionnaireName: undefined,
  type: 'speakUps',
  questionLabel: undefined,
  score: undefined,
  hideResponseSection: false,
};
